import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useLocation, useNavigate } from "react-router";
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useAppData } from "store/appData";
import { cdnImageBase, cdnImageUrl, type ImageAnnotation } from "../api";
import { useDatasetDetail } from "./DatasetDetailContext";
import AnnotationOverlay from "./AnnotationOverlay";
import UnsuperverifyButton from "./UnsuperverifyButton";
import SpeedImage from "components/SpeedImage";
import t from "../translations";
import {
  fitBox,
  findAnnotationAt,
  resolveAnnotationTarget,
  type ImageBox,
  type Size,
} from "./imagePanelUtils";

// Per-wheel-tick scale change. Matches the regular app's default zoomSpeed.
const ZOOM_STEP = 0.17;
// Hard limits relative to the fit-to-viewport box.
const MIN_FIT_RATIO = 1;
const MAX_FIT_RATIO = 20;
// Pointer travel beyond this counts as a pan; below it a click can speed-zoom.
const DRAG_THRESHOLD = 5;
// Fraction of the viewport a speed-zoomed annotation fills on its long axis.
const SPEED_ZOOM_OCCUPANCY = 0.5;

export default function DatasetImagePanel() {
  const {
    selectedImage,
    annotations,
    datasetTypeList,
    loading,
    datasetId,
    getAnnotationColor,
    detailBasePath,
    setImageClosed,
  } = useDatasetDetail();
  const { objectTypes, workflowStatuses, projects, domains } = useAppData();
  const navigate = useNavigate();
  const location = useLocation();
  const [imageError, setImageError] = useState<number | string | null>(null);

  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [containerSize, setContainerSize] = useState<Size>({
    width: 0,
    height: 0,
  });
  const [naturalSize, setNaturalSize] = useState<Size | null>(null);
  const [userTransform, setUserTransform] = useState<ImageBox | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const speedZoomedIdRef = useRef<number | null>(null);

  const selectedImageId = selectedImage?.id ?? null;
  const dbWidth = selectedImage?.width ?? null;
  const dbHeight = selectedImage?.height ?? null;

  const fullSrc = selectedImage ? cdnImageUrl(selectedImage) : "";

  const visibleAnnotations = useMemo<ImageAnnotation[]>(() => {
    // Empty type_list = no restriction (ad-hoc/test datasets).
    if (datasetTypeList.length === 0) return annotations;
    const allowed = new Set(datasetTypeList);
    return annotations.flatMap((annotation) => {
      const types = annotation.types.filter((type) =>
        allowed.has(type.object_type_id)
      );
      return types.length > 0 ? [{ ...annotation, types }] : [];
    });
  }, [annotations, datasetTypeList]);

  // Reset transient state and (re)seed natural size whenever the image or its
  // stored dimensions change. Keyed by id so a parent refetch returning the
  // same image doesn't clobber zoom/pan.
  useEffect(() => {
    setImageError(null);
    setUserTransform(null);
    setHoveredId(null);
    setDragging(false);
    speedZoomedIdRef.current = null;
    setNaturalSize(
      dbWidth && dbHeight ? { width: dbWidth, height: dbHeight } : null
    );
  }, [selectedImageId, dbWidth, dbHeight]);

  useEffect(() => {
    if (!containerEl) return;
    const update = () => {
      const rect = containerEl.getBoundingClientRect();
      setContainerSize((prev) => {
        const width = Math.round(rect.width);
        const height = Math.round(rect.height);
        if (prev.width === width && prev.height === height) return prev;
        return { width, height };
      });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(containerEl);
    return () => observer.disconnect();
  }, [containerEl]);

  // Cached transform is in viewport pixels; drop it on resize and clear the
  // speed-zoom marker so the next click re-zooms instead of toggling off.
  useEffect(() => {
    setUserTransform(null);
    speedZoomedIdRef.current = null;
  }, [containerSize.width, containerSize.height]);

  const imageBox = useMemo<ImageBox | null>(() => {
    if (userTransform) return userTransform;
    if (!containerSize.width || !containerSize.height) return null;
    if (naturalSize) return fitBox(containerSize, naturalSize);
    // Fill the viewport so the low tile renders and reports its size.
    return {
      x: 0,
      y: 0,
      width: containerSize.width,
      height: containerSize.height,
    };
  }, [userTransform, containerSize, naturalSize]);

  // Ref mirror lets the wheel handler compose bursts without re-binding per render.
  const imageBoxRef = useRef<ImageBox | null>(null);
  imageBoxRef.current = imageBox;

  const handleLoad = useCallback(
    ({
      naturalWidth,
      naturalHeight,
    }: {
      naturalWidth: number;
      naturalHeight: number;
    }) => {
      setNaturalSize((prev) =>
        prev ? prev : { width: naturalWidth, height: naturalHeight }
      );
    },
    []
  );

  // Ignore HEAD results once the user has moved on to a different image.
  const errorFetchSrcRef = useRef<string | null>(null);
  const handleImageError = useCallback(async () => {
    if (!fullSrc) {
      setImageError("Source Missing");
      return;
    }
    const requestSrc = fullSrc;
    errorFetchSrcRef.current = requestSrc;
    try {
      const res = await fetch(requestSrc, {
        method: "HEAD",
        credentials: "include",
      });
      if (errorFetchSrcRef.current !== requestSrc) return;
      setImageError(res.ok ? null : res.status);
    } catch (error: unknown) {
      if (errorFetchSrcRef.current !== requestSrc) return;
      const message =
        error instanceof Error ? error.message : "Network error";
      setImageError(message);
    }
  }, [fullSrc]);

  function getErrorMessage(): string {
    if (imageError === 404) return t.ImageErrorNotFound;
    if (imageError === 401 || imageError === 403)
      return t.ImageErrorUnauthorized;
    return t.ImageErrorGeneric(imageError ?? "unknown");
  }

  // addEventListener with passive: false so preventDefault stops page scroll.
  useEffect(() => {
    if (!containerEl || !naturalSize) return;
    const el = containerEl;
    const onWheel = (event: WheelEvent) => {
      // Horizontal-only scrolls would otherwise default to zoom-in.
      if (event.deltaY === 0) return;
      const current = imageBoxRef.current;
      if (!current) return;
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      const cursorX = event.clientX - rect.left;
      const cursorY = event.clientY - rect.top;

      const direction = event.deltaY > 0 ? -1 : 1;
      let scale = 1 + ZOOM_STEP * direction;

      const fit = fitBox(containerSize, naturalSize);
      if (!fit.width) return;
      const currentRatio = current.width / fit.width;
      const newRatio = currentRatio * scale;
      if (newRatio < MIN_FIT_RATIO) scale = MIN_FIT_RATIO / currentRatio;
      if (newRatio > MAX_FIT_RATIO) scale = MAX_FIT_RATIO / currentRatio;
      if (Math.abs(scale - 1) < 1e-6) return;

      const next: ImageBox = {
        x: cursorX - (cursorX - current.x) * scale,
        y: cursorY - (cursorY - current.y) * scale,
        width: current.width * scale,
        height: current.height * scale,
      };
      // Sync so a follow-up wheel event in the same microtask sees this update.
      imageBoxRef.current = next;
      setUserTransform(next);
      speedZoomedIdRef.current = null;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [naturalSize, containerSize, containerEl]);

  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
    dragged: boolean;
    modifier: boolean;
  } | null>(null);

  const hitTestPointer = useCallback(
    (clientX: number, clientY: number): ImageAnnotation | null => {
      if (!imageBox || !containerEl) return null;
      const rect = containerEl.getBoundingClientRect();
      const px = (clientX - rect.left - imageBox.x) / imageBox.width;
      const py = (clientY - rect.top - imageBox.y) / imageBox.height;
      return findAnnotationAt(px, py, visibleAnnotations);
    },
    [imageBox, visibleAnnotations, containerEl]
  );

  const speedZoomTo = useCallback(
    (annotation: ImageAnnotation | null) => {
      if (!naturalSize || !containerSize.width || !containerSize.height) return;
      // A miss only unwinds an active speed-zoom, never a wheel-zoom.
      if (!annotation) {
        if (speedZoomedIdRef.current !== null) {
          setUserTransform(null);
          speedZoomedIdRef.current = null;
        }
        return;
      }
      if (annotation.id === speedZoomedIdRef.current) {
        setUserTransform(null);
        speedZoomedIdRef.current = null;
        return;
      }
      const fit = fitBox(containerSize, naturalSize);
      if (!fit.width) return;
      const minScale = fit.width / naturalSize.width;
      const maxScale = minScale * MAX_FIT_RATIO;
      const widthScale =
        (SPEED_ZOOM_OCCUPANCY * containerSize.width) /
        (annotation.width * naturalSize.width);
      const heightScale =
        (SPEED_ZOOM_OCCUPANCY * containerSize.height) /
        (annotation.height * naturalSize.height);
      const targetScale = Math.min(widthScale, heightScale);
      const scale = Math.max(minScale, Math.min(maxScale, targetScale));

      const width = naturalSize.width * scale;
      const height = naturalSize.height * scale;
      const centerX = (annotation.x + annotation.width / 2) * width;
      const centerY = (annotation.y + annotation.height / 2) * height;
      setUserTransform({
        x: containerSize.width / 2 - centerX,
        y: containerSize.height / 2 - centerY,
        width,
        height,
      });
      speedZoomedIdRef.current = annotation.id;
    },
    [containerSize, naturalSize]
  );

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!imageBox || event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      dragged: false,
      modifier: event.shiftKey || event.ctrlKey || event.metaKey,
    };
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      const hit = hitTestPointer(event.clientX, event.clientY);
      setHoveredId((prev) => {
        const next = hit ? hit.id : null;
        return prev === next ? prev : next;
      });
      return;
    }

    if (!drag.dragged) {
      const totalDx = event.clientX - drag.startX;
      const totalDy = event.clientY - drag.startY;
      const distance = Math.sqrt(totalDx * totalDx + totalDy * totalDy);
      if (distance < DRAG_THRESHOLD) return;
      drag.dragged = true;
      setDragging(true);
      setHoveredId(null);
    }
    const dx = event.clientX - drag.lastX;
    const dy = event.clientY - drag.lastY;
    drag.lastX = event.clientX;
    drag.lastY = event.clientY;
    setUserTransform((prev) => {
      const base = prev ?? imageBox;
      if (!base) return prev;
      return { ...base, x: base.x + dx, y: base.y + dy };
    });
    speedZoomedIdRef.current = null;
  };

  const finishDrag = (
    event: ReactPointerEvent<HTMLDivElement>,
    cancelled: boolean
  ) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const { dragged, modifier } = drag;
    dragRef.current = null;
    setDragging(false);

    if (cancelled || dragged || !modifier) return;
    speedZoomTo(hitTestPointer(event.clientX, event.clientY));
  };

  const handlePointerLeave = () => {
    if (!dragRef.current) setHoveredId(null);
  };

  if (loading && !selectedImage) {
    return (
      <Container>
        <CircularProgress />
      </Container>
    );
  }

  if (!selectedImage) {
    return (
      <Container>
        <Typography color="text.secondary">{t.ImageNotFound}</Typography>
      </Container>
    );
  }

  const tileBase = cdnImageBase(selectedImage);
  const imagePosition = imageBox
    ? {
        topLeft: { x: imageBox.x, y: imageBox.y },
        bottomRight: {
          x: imageBox.x + imageBox.width,
          y: imageBox.y + imageBox.height,
        },
      }
    : null;

  const annotationTarget = resolveAnnotationTarget(
    selectedImage,
    projects,
    domains,
  );

  const closeImage = () => {
    setImageClosed(true);
    navigate(
      { pathname: `${detailBasePath}/${datasetId}`, search: location.search },
      { replace: true },
    );
  };

  return (
    <Container data-testid="datasetImagePanel.container">
      <Box
        ref={setContainerEl}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={(e) => finishDrag(e, false)}
        onPointerCancel={(e) => finishDrag(e, true)}
        onPointerLeave={handlePointerLeave}
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          userSelect: "none",
          cursor:
            imageError !== null ? "default" : dragging ? "grabbing" : "grab",
        }}
      >
        {imageError !== null ? (
          <Typography
            color="text.secondary"
            data-testid="datasetImagePanel.error"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            {getErrorMessage()}
          </Typography>
        ) : (
          imagePosition &&
          imageBox && (
            <>
              <Box
                data-testid="datasetImagePanel.image"
                sx={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  // Trap tile z-indices so the annotation overlay paints above.
                  isolation: "isolate",
                  filter: selectedImage.modified_since_integration
                    ? "grayscale(1)"
                    : undefined,
                }}
              >
                <SpeedImage
                  imagePosition={imagePosition}
                  baseUrl={tileBase.baseUrl}
                  extension={tileBase.extension}
                  // DB-only: the low tile's width (capped at 2000) would lock out the high tier.
                  sourceWidth={dbWidth}
                  viewportWidth={containerSize.width}
                  viewportHeight={containerSize.height}
                  onLoad={handleLoad}
                  onError={handleImageError}
                />
              </Box>
              {naturalSize && (
                <AnnotationOverlay
                  annotations={visibleAnnotations}
                  objectTypes={objectTypes}
                  workflowStatuses={workflowStatuses}
                  getAnnotationColor={getAnnotationColor}
                  box={imageBox}
                  hoveredId={hoveredId}
                />
              )}
            </>
          )
        )}
        <Stack
          data-testid="datasetImagePanel.actions"
          onPointerDown={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            flexDirection: "row",
            alignItems: "center",
            gap: 0.25,
            p: 0.25,
            borderRadius: 1,
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            boxShadow: 2,
          }}
        >
          <Tooltip
            title={
              annotationTarget
                ? t.OpenInAnnotationToolOn(annotationTarget.domainName)
                : t.AnnotationToolUnavailable
            }
            arrow
          >
            <Box component="span">
              <IconButton
                component="a"
                size="small"
                href={annotationTarget?.url ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                disabled={annotationTarget === null}
                data-testid="datasetImagePanel.openAnnotationTool"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <UnsuperverifyButton />
          <Tooltip title={t.CloseImage} arrow>
            <IconButton
              size="small"
              onClick={closeImage}
              data-testid="datasetImagePanel.closeImage"
              aria-label={t.CloseImage}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Container>
  );
}

function Container({
  children,
  ...props
}: PropsWithChildren<{ "data-testid"?: string }>) {
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 0,
        height: "100%",
        bgcolor: "background.default",
        alignItems: "center",
        justifyContent: "center",
      }}
      {...props}
    >
      {children}
    </Box>
  );
}
